
import {
  payPlanChangeRequestMapActions,
  payPlanChangeRequestMapGetters,
  payPlanChangeRequestMapState
} from "@/store/modules/payplanchangerequest";
import { tableColumnsListBillingChangeRequests } from "./TableColumns";
import PayPlanChangeRequestTableActionsVue from "../../ChangeRequestTableActions/ChangeRequestTableActions.vue";
import NoResultsFound from "@/components/NotFound/NoResultsFound.vue";
import { get } from "lodash";
import Charts from "@/components/Charts/Charts.vue";
import ChangeRequestMixin from "@/components/ChangeRequests/ChangeRequestMixin";
import { clearSelectionsNotInData } from "@/helpers";
import VueWithMixins from "@/helpers/mixins";
import { getSystemFilesURL } from "@/helpers";
import { getPrintMethod } from "@/helpers/printMethodHelper";
import PrintMethodModal from "@/components/Dialog/PrintMethodModal.vue";

export default VueWithMixins(ChangeRequestMixin).extend({
  name: "PayPlanChangeRequestsDashboard",
  components: {
    Exporter: () => import("@/components/Exporter/Exporter.vue"),
    Charts,
    NoResultsFound,
    PrintMethodModal
  },
  data() {
    return {
      tableColumns: tableColumnsListBillingChangeRequests,
      pageKey: "Pay Plan",
      exportModalKey: "exportPayPlanChangeRequestsModal",
      deleteModalKey: "payPlanChangeRequestDeleteModal",
      activityRoute: "/admin/payplanchangerequests/activities",
      changeRequestRoute: "payplanchangerequest",
      payPlanChangeRequestNotFoundImage: ""
    };
  },
  async mounted() {
    try {
      const url = await getSystemFilesURL("pay-plan");
      if (url) {
        this.payPlanChangeRequestNotFoundImage = url;
      }
    } catch (error) {
      this.$bugSnagClient.notify(error);
    }
  },
  methods: {
    // get store methods
    ...payPlanChangeRequestMapActions([
      "deletePayPlanChangeRequest",
      "printPayPlanChangeRequest",
      "updatePayPlanChangeRequestsStatus",
      "deleteBulkChangeRequests",
      "restorePayPlanChangeRequests"
    ]),
    ...payPlanChangeRequestMapActions(["getPayPlanChangeRequests"]),
    async bulkActionHandler(action: string) {
      switch (action) {
        case "Print Selected": {
          try {
            let message = "Only valid pay plan change requests will be printed";
            let messageType = "info";
            const documentIds: string[] = this.getSelectedDocumentIds;

            if (documentIds.length === 0) {
              message = "No Change request selected";
              messageType = "error";
            }
            this.$appNotify({
              title: "Please note:",
              type: messageType,
              message: message,
              offset: 100,
              position: "top-right"
            });

            const printMethod = await getPrintMethod(this);
            await this.printCallback(
              documentIds,
              this.printPayPlanChangeRequest,
              "pay plan",
              undefined,
              printMethod
            );
          } catch (error) {
            this.$appNotifyError(error.message);
            this.$bugSnagClient.notify(error);
          } finally {
            this.loading = false;
            this.loadingText = "";
          }
          break;
        }
        case "Delete Selected": {
          try {
            this.loadingText = "Deleting Change Requests...";
            const ids = this.selectedDocuments.map((doc: any) => doc._id);
            await this.deleteBulkChangeRequests(ids);
            this.$appNotifySuccess("Selected Requests Deleted");
            this.fetchPageData(
              this.$store.state.list_view_module["filterQuery"] || {}
            );
          } catch (error) {
            this.errorMessage = error.message;
            this.$bugSnagClient.notify(error);
          }
          break;
        }
        case "Restore Selected": {
          try {
            this.loadingText = "Restoring selected change requests...";
            const ids = this.selectedDocuments.map((doc: any) => doc._id);
            await this.restorePayPlanChangeRequests({ ids });
            this.$appNotifySuccess("Selected Requests Restored");
            this.fetchPageData(
              this.$store.state.list_view_module["filterQuery"] || {}
            );
          } catch (error) {
            this.errorMessage = error.message;
            this.$bugSnagClient.notify(error);
          }
          break;
        }
        default:
          break;
      }
    },
    async handleResponseAction(payload: any) {
      const { data } = payload;
      data.isLegacy = this.queryOverride.isLegacy;
      const policyPrefix =
        data &&
        data.data &&
        data.data.policyInfo &&
        data.data.policyInfo.policyPrefix
          ? data.data.policyInfo.policyPrefix
          : undefined;

      switch (payload.name) {
        case "print": {
          const printMethod = await getPrintMethod(this);
          this.printCallback(
            data._id,
            this.printPayPlanChangeRequest,
            "pay plan",
            policyPrefix,
            printMethod
          );
          break;
        }
        case "delete":
          this.deleteCallback(data._id);
          break;
        case "view":
          this.viewChangeRequest(data);
          break;
        case "edit":
          if (
            (data.status === "Not Submitted" ||
              (get(this.getCurrentUser, "role") === "admin" &&
                get(data, "status") !== "Received" &&
                !get(data, "printed"))) &&
            !data.isLegacy
          ) {
            this.$router
              .push(`/payplanchangerequest/edit/${data._id}`)
              .catch(() => {});
            break;
          } else {
            this.viewChangeRequest(data);
            break;
          }
        case "restore":
          this.restoreChangeRequests(data._id);
          break;

        case "activities":
          this.viewChangeRequest(data, "activities");
          break;
        default:
          this.errorMessage = "Operation specified is not supported";
      }
    },
    async restoreChangeRequests(id: string): Promise<any> {
      try {
        this.loading = true;
        this.loadingText = "Restoring Change Requests...";
        await this.restorePayPlanChangeRequests({ ids: [id] });
        this.successMessage = "Claim Successfully Restored";
        this.fetchPageData({});
      } catch (e) {
        this.errorMessage = e.message;
      } finally {
        this.loading = false;
      }
    },
    async deleteOnePayPlanChangeRequest(): Promise<void> {
      await this.deletePayPlanChangeRequest(this.changeRequestId);
      this.$modal.hide("payPlanChangeRequestDeleteModal");
      this.$appNotifySuccess("Pay Plan Change Request Deleted Successfully.");
    },
    getPayPlanChangeRequestOperations() {
      return PayPlanChangeRequestTableActionsVue;
    },
    async fetchPageData(queryObject: any): Promise<void> {
      const { __limit: limit, __sort: sort } = queryObject;
      if (!limit) {
        queryObject = {
          ...queryObject,
          __limit: 50
        };
      }
      const sortFormats: Record<string, string> = {
        "-submittedOnFormatted": "-submittedOn",
        submittedOnFormatted: "submittedOn",
        "-modifiedOnFormatted": "-modifiedOn",
        modifiedOnFormatted: "modifiedOn"
      };
      if (sort) {
        queryObject.__sort = sortFormats[sort];
      }
      try {
        this.loadingText = "Fetching Pay Plan Change Requests...";
        this.loading = true;
        this.$emit("scrollTop");
        await this.getPayPlanChangeRequests({
          query: { ...queryObject, ...this.queryOverride }
        });
        this.selectedDocuments = clearSelectionsNotInData(
          this.getPayPlanChangeRequestsFromStore,
          this.selectedDocuments
        );
      } catch (error) {
        this.errorMessage =
          "Error fetching Pay Plan change requests. Try again later";
        this.$bugSnagClient.notify(error);
      } finally {
        this.loading = false;
        this.reserveSelection = true;
      }
    },
    async customBulkActionHandler(action: string) {
      this.reserveSelection = false;
      switch (action) {
        case "Not Submitted":
        case "Submitted":
        case "Received":
        case "Pending":
        case "Issued": {
          try {
            await this.updateStatus(
              action,
              this.updatePayPlanChangeRequestsStatus
            );
            this.fetchPageData(
              this.$store.state.list_view_module["filterQuery"] || {}
            );
          } catch (error) {
            this.$appNotifyError(error.message);
            this.$bugSnagClient.notify(error);
          }
          break;
        }
        default:
          break;
      }
    }
  },
  computed: {
    ...payPlanChangeRequestMapGetters([
      "getPayPlanChangeRequestsFromStore",
      "getPayPlanChangeRequestsLinks",
      "getTotalRecords",
      "getMaxQuerySize"
    ]),
    ...payPlanChangeRequestMapState(["makingApiRequest"]),
    tableActions(): string[] {
      return this.getCurrentUser && this.getCurrentUser.role === "admin"
        ? ["Export Pay Plan Change Requests", "All Activities"].sort()
        : [];
    },
    chart(): string {
      return this.getChart("pay-plan-change-status");
    }
  }
});
